import React, { useContext, useState, useEffect, createContext } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc, setDoc, query, where, getDocs, serverTimestamp, collection, updateDoc, writeBatch } from "firebase/firestore";
import { auth, db, googleProvider } from "../firebase/firebase";

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [dnaProfile, setDnaProfile] = useState(null);
  const [assessment, setAssessment] = useState(null);
  // claims: Firebase Auth custom claims attached to the ID token.
  // claims.admin === true and claims.role === 'agency' are the
  // authoritative source for authorization (Firestore rules read
  // request.auth.token.admin / .role). The role field on the user
  // doc is a denormalized UI hint only.
  const [claims, setClaims] = useState({});
  const [loading, setLoading] = useState(true);

  const hasCompletedAssessment = !!dnaProfile;

  async function handleUserAuth(user, navigate, options = {}) {
    const { role = 'founder', partnerId = null, name = null, phone = null } = options;
    const userDocRef = doc(db, "users", user.uid);
    const userDocSnap = await getDoc(userDocRef);

    let finalRole = role;
    let finalPartnerId = partnerId;

    // Check for a pending invitation first, as this is the source of truth for partnership
    const invitationQuery = query(collection(db, "pendingInvitations"), where("clientEmail", "==", user.email));
    const invitationSnapshot = await getDocs(invitationQuery);

    if (!invitationSnapshot.empty) {
        const invitationDoc = invitationSnapshot.docs[0];
        const invitationData = invitationDoc.data();
        finalPartnerId = invitationData.partnerId; // This partnerId takes precedence

        const partnerClientQuery = query(collection(db, "partnerClients"),
            where("partnerId", "==", finalPartnerId),
            where("clientEmail", "==", user.email));
        const partnerClientSnapshot = await getDocs(partnerClientQuery);

        if (!partnerClientSnapshot.empty) {
            const partnerClientDocRef = partnerClientSnapshot.docs[0].ref;
            const batch = writeBatch(db);
            batch.update(partnerClientDocRef, { status: "active", clientUserId: user.uid });
            batch.delete(invitationDoc.ref);
            await batch.commit();
        }
    }

    if (userDocSnap.exists()) {
        const existingData = userDocSnap.data();
        finalRole = existingData.role || 'founder'; // Use existing role
        // Update partnerId if it's not already set
        if (finalPartnerId && !existingData.partnerId) {
            await updateDoc(userDocRef, { partnerId: finalPartnerId });
        }
    } else {
        // New users always get role: 'founder' on the user doc.
        // Agency elevation now happens exclusively via the
        // setAgencyRole Cloud Function (which writes the custom
        // claim that Firestore rules check). The Firestore create
        // rule rejects any other role value — closes F-001.
        finalRole = 'founder';
        await setDoc(userDocRef, {
            name: name || user.displayName || "",
            email: user.email,
            role: 'founder',
            organizationId: null,
            assignedHubs: [],
            assessmentCompleted: false,
            partnerId: finalPartnerId,
            createdAt: serverTimestamp(),
            trialStartedAt: serverTimestamp(),
            subscriptionStatus: 'trialing',
            phone: phone || null,
            country: 'IN',
            profileComplete: !!phone,
        });
        if (name && !user.displayName) {
            await updateProfile(user, { displayName: name });
        }
    }

    const uData = (await getDoc(userDocRef)).data();
    setUserData(uData);

    if (uData.role === 'agency') {
        navigate('/partner-dashboard');
    } else if (uData.profileComplete === false) {
        navigate('/complete-profile');
    } else {
        navigate('/dashboard');
    }
  }

  async function signUp(email, password, name, phone, role, partnerId, navigate) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await handleUserAuth(result.user, navigate, { role, partnerId, name, phone });
    return result;
  }

  async function signIn(email, password, navigate) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    await handleUserAuth(result.user, navigate);
    return result;
  }

  async function signInWithGoogle(navigate, role, partnerId) {
    const result = await signInWithPopup(auth, googleProvider);
    await handleUserAuth(result.user, navigate, { role, partnerId });
    return result;
  }

  async function signOut(navigate) {
    // Always clear local state and navigate, even if Firebase signOut errors
    // (network blip, etc.). try/finally guarantees the user is not stuck on
    // a protected page after their session was already invalidated -- F-023.
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out from Firebase:', error);
    } finally {
      setCurrentUser(null);
      setUserData(null);
      setOrganization(null);
      setDnaProfile(null);
      setAssessment(null);
      setClaims({});
      navigate('/');
    }
  }

  async function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          // Fetch custom claims from the ID token. This is what
          // Firestore rules see at request.auth.token.* — admin and
          // role checks in the React tree should read from `claims`,
          // not from userData.role (which is a UI hint and may lag
          // the claim until token refresh / re-login).
          const tokenResult = await user.getIdTokenResult();
          setClaims(tokenResult.claims || {});

          const userDocRef = doc(db, "users", user.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            const uData = userDocSnap.data();
            setUserData(uData);
            if (uData.organizationId) {
              const orgDocRef = doc(db, "organizations", uData.organizationId);
              const orgDocSnap = await getDoc(orgDocRef);
              if (orgDocSnap.exists()) {
                setOrganization(orgDocSnap.data());
              }
            }
            // dnaProfiles is keyed by uid (see saveDNAProfile in firestore.js),
            // so direct doc fetch is correct -- F-025.
            const dnaProfileSnap = await getDoc(doc(db, "dnaProfiles", user.uid));
            if (dnaProfileSnap.exists()) {
              setDnaProfile(dnaProfileSnap.data());
            }
            const assessmentQuery = query(collection(db, "assessments"), where("userId", "==", user.uid));
            const assessmentSnapshot = await getDocs(assessmentQuery);
            if (!assessmentSnapshot.empty) {
              setAssessment(assessmentSnapshot.docs[0].data());
            }
          }
        } catch (error) {
          console.error("Error fetching user data:", error);
        }
      } else {
        setUserData(null);
        setOrganization(null);
        setDnaProfile(null);
        setAssessment(null);
        setClaims({});
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userData,
    organization,
    dnaProfile,
    claims,
    loading,
    hasCompletedAssessment,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
